import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SellerAvatar from "@/components/SellerAvatar";
import { api } from "@/lib/api";

/**
 * HallOfBeacons — public archive of every weekly Beacon winner,
 * grouped by ISO week, then split by category. This is the page
 * sellers brag-link from their social profiles. Designed to be
 * screenshot-friendly: dark, prestigious, gold accents.
 */
export default function HallOfBeacons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/beacon/winners?limit=100")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group by week
  const byWeek = items.reduce((acc, w) => {
    acc[w.week_iso] = acc[w.week_iso] || [];
    acc[w.week_iso].push(w);
    return acc;
  }, {});
  const weeks = Object.keys(byWeek).sort().reverse();

  return (
    <div
      className="min-h-screen flex flex-col bg-[#070a15] text-white"
      data-testid="hall-of-beacons-page"
    >
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-12">
          <p className="overline text-amber-300 mb-3">The Hall</p>
          <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter">
            Hall of Beacons
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-white/70">
            Every seller who's ever lit up the Bidorah homepage. The Beacon is
            earned, not bought — and once you're in the hall, you're there
            forever.
          </p>
        </div>

        {loading && (
          <p className="text-center text-white/40 py-20">Loading the hall…</p>
        )}

        {!loading && !weeks.length && (
          <div
            className="border border-amber-300/20 rounded-2xl p-10 text-center text-white/60"
            data-testid="hall-empty"
          >
            <p className="font-display font-bold text-2xl mb-2">
              The first chapter is being written.
            </p>
            <p className="text-sm">
              The Beacon snapshots a new set of winners every Monday. Be the
              first name in the hall.
            </p>
            <Link
              to="/apply-to-sell"
              className="inline-flex mt-6 rounded-full bg-amber-300 text-amber-950 px-5 py-2 text-sm font-black"
            >
              Apply to sell →
            </Link>
          </div>
        )}

        {weeks.map((wk) => (
          <section key={wk} className="mb-12" data-testid={`hall-week-${wk}`}>
            <div className="flex items-center gap-3 mb-5">
              <h2 className="font-display font-black text-2xl tracking-tighter text-amber-300">
                {wk}
              </h2>
              <span className="flex-1 h-px bg-gradient-to-r from-amber-300/40 to-transparent" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {byWeek[wk].map((w) => (
                <Link
                  key={w.id}
                  to={`/seller/${w.user_id}`}
                  data-testid={`hall-winner-${w.id}`}
                  className="group block rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 to-zinc-950 p-5 hover:border-amber-300/50 hover:shadow-[0_0_40px_rgba(252,211,77,0.15)] transition"
                >
                  <p className="text-[10px] uppercase tracking-widest text-amber-300/80 mb-3">
                    {w.category_label}
                  </p>
                  <div className="flex items-center gap-3">
                    <SellerAvatar
                      picture={w.picture}
                      name={w.username}
                      size={64}
                      className="ring-2 ring-amber-300/50"
                    />
                    <div className="min-w-0">
                      <p className="font-display font-black text-xl tracking-tighter group-hover:text-amber-200 truncate">
                        @{w.username}
                      </p>
                      {w.is_founder && (
                        <span className="text-[10px] uppercase tracking-widest text-amber-300/80">
                          Founder Seller
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>
      <Footer />
    </div>
  );
}
